import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { analysisService } from '../../services/analysis.service';
import { Loader2Icon, CheckCircle2Icon, XCircleIcon, LayoutDashboardIcon, MaximizeIcon } from 'lucide-react';
import { showInConciseMode } from '../chat/threadSettings';
import { cn } from '@/lib/utils';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { DashboardViewerOverlay } from './dashboard-viewer-overlay';
import { useSidePanel } from '../chat/SidePanel';
import { DASHBOARD_SIDE_PANEL_KIND } from './dashboard-side-panel';

interface DashboardToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
}

function parseJson(value: unknown): any {
  if (value === undefined || value === null) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value as string);
  } catch {
    return null;
  }
}

const DashboardToolCallComponent: React.FC<DashboardToolCallProps> = ({
  toolName,
  args,
  argsText,
  result,
  status,
}) => {
  // Prefer the conversation's side panel so the thread stays open and the user
  // can keep asking for changes. Falls back to a full-screen overlay where there
  // is no panel — an embed, or a thread rendered outside the Ellie page.
  const sidePanel = useSidePanel();
  const [overlayOpen, setOverlayOpen] = useState(false);
  const queryClient = useQueryClient();

  const isUpdate = toolName === 'update_dashboard';
  const isRunning = status?.type === 'running' || (status === undefined && result === undefined);
  const isError = status?.type === 'incomplete' && status.reason === 'error';
  const isComplete = !isRunning && !isError && result !== undefined;

  const parsedArgs = parseJson(args) ?? parseJson(argsText) ?? {};
  const parsedResult = isComplete ? parseJson(result) : null;

  const dashboardId: string | undefined = parsedResult?.dashboard_id;
  const errorMessage: string | undefined = parsedResult?.error;
  // A tool that returned neither an id nor an error didn't do what it claimed.
  const hasError = isError || (isComplete && !dashboardId);

  const name: string = parsedResult?.name || parsedArgs?.name || 'Dashboard';
  const panelCount: number | undefined =
    parsedResult?.panel_count ?? (Array.isArray(parsedArgs?.panels) ? parsedArgs.panels.length : undefined);

  const buildOrchestrationId: string | undefined = parsedResult?.build_orchestration_id;
  const panelShowsThisDashboard = !!dashboardId
    && !!sidePanel?.isOpen(DASHBOARD_SIDE_PANEL_KIND, dashboardId);

  // The panel is showing a dashboard this tool call just rewrote, so its cached
  // copy is stale. Refetching here is what makes the right-hand side track the
  // conversation instead of needing a reload.
  useEffect(() => {
    if (!isComplete || !panelShowsThisDashboard) return;
    queryClient.invalidateQueries({ queryKey: ['dashboard', dashboardId] });
  }, [isComplete, panelShowsThisDashboard, dashboardId, queryClient]);

  // Refreshing the document alone is not enough once a change triggers a build:
  // the new panels arrive pointing at tables that do not exist yet, render "this
  // chart hasn't been built yet", and stay that way until someone hits Refresh.
  // Follow the build and invalidate the data when it lands, so the panels fill
  // in on their own.
  useEffect(() => {
    if (!isComplete || !panelShowsThisDashboard || !buildOrchestrationId) return;

    let cancelled = false;
    const TERMINAL = ['COMPLETE', 'ERROR', 'IN_REVIEW', 'REJECTED', 'CANCELLED'];
    // Same cadence and ceiling the dashboard's own Refresh uses.
    const INTERVAL_MS = 2000;
    const MAX_ATTEMPTS = 120;

    (async () => {
      for (let i = 0; i < MAX_ATTEMPTS && !cancelled; i++) {
        await new Promise((r) => setTimeout(r, INTERVAL_MS));
        if (cancelled) return;
        try {
          const orchestration = await analysisService.getOrchestrationStatus(buildOrchestrationId);
          if (!TERMINAL.includes(orchestration.status)) continue;
          if (cancelled) return;
          // Even on ERROR: the panels should show whatever did build, and their
          // own error states, rather than a stale "not built yet".
          queryClient.invalidateQueries({ queryKey: ['dashboard', dashboardId] });
          queryClient.invalidateQueries({ queryKey: ['analysisData'] });
          return;
        } catch {
          // A transient failure polling the build is not worth surfacing — but
          // it must not end the watch either. `return` here meant one dropped
          // request stopped the poll for good, and the panels sat on "not built
          // yet" through a build that finished fine. The comment claimed the
          // next tick retried; now it does.
          continue;
        }
      }
    })();

    // Closing the panel, switching dashboards or unmounting the step stops it.
    return () => { cancelled = true; };
  }, [isComplete, panelShowsThisDashboard, buildOrchestrationId, dashboardId, queryClient]);

  const openDashboard = () => {
    if (!dashboardId) return;
    if (sidePanel) {
      sidePanel.open({ kind: DASHBOARD_SIDE_PANEL_KIND, id: dashboardId, title: name });
      return;
    }
    setOverlayOpen(true);
  };

  const verb = isUpdate ? 'Updating' : 'Building';
  const pastVerb = isUpdate ? 'Updated' : 'Created';

  return (
    <>
      <Card>
        <div className={cn(
          'p-3',
          isRunning
            ? 'bg-primary/10 border-b border-primary/20'
            : 'bg-muted/50 border-b border-border'
        )}>
          <div className="flex items-center gap-3">
            <div className="flex-1 space-y-1 min-w-0">
              {isRunning ? (
                <div className="flex items-center gap-2">
                  <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
                  <p className="text-sm m-0 text-foreground truncate">
                    {verb} dashboard{name ? `: ${name}` : ''}...
                  </p>
                </div>
              ) : hasError ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <LayoutDashboardIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <p className="text-sm m-0 text-muted-foreground">
                      {isUpdate ? 'Update Dashboard' : 'Create Dashboard'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
                    <p className="text-sm m-0 text-destructive">
                      {errorMessage || `Failed to ${isUpdate ? 'update' : 'create'} dashboard`}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <LayoutDashboardIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <p className="text-sm m-0 text-muted-foreground">
                      {isUpdate ? 'Update Dashboard' : 'Create Dashboard'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
                    <p className="text-sm m-0 text-foreground truncate">
                      {pastVerb} "{name}"
                      {typeof panelCount === 'number' ? ` — ${panelCount} panel${panelCount === 1 ? '' : 's'}` : ''}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {dashboardId && !hasError && (
              <Button
                size="sm"
                variant="outline"
                className="flex-shrink-0"
                onClick={openDashboard}
              >
                <MaximizeIcon className="h-4 w-4 mr-1" />
                View Dashboard
              </Button>
            )}
          </div>
        </div>
      </Card>

      {overlayOpen && dashboardId && (
        <DashboardViewerOverlay
          dashboardId={dashboardId}
          name={name}
          onClose={() => setOverlayOpen(false)}
        />
      )}
    </>
  );
};

export const DashboardToolCall = DashboardToolCallComponent as React.ComponentType<any>;

// The dashboard is the deliverable, and the View button is the only way to open
// it without leaving the thread — never collapse this one away.
showInConciseMode(DashboardToolCall);
